import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ScanLine, PlusCircle, MinusCircle, ArrowLeft, Tags, Check } from '@/components/icons';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { useToast } from '@/components/ui/useToast';
import {
  adjustWarehouseStock,
  prepareAdjustmentLabels,
  commitAdjustmentAddition,
  discardAdjustmentLabels,
  deductAdjustmentStock,
  listWarehouseSerials,
  type WarehouseStockRow,
} from '@/api/warehouseStock';
import { listLocations } from '@/api/locations';
import { useFieldErrors } from '@/lib/useFieldErrors';
import { apiError } from '@/lib/apiError';
import { AdjustLabelsModal, type AdjustLabelItem } from './AdjustLabelsModal';

interface Props {
  open: boolean;
  row: WarehouseStockRow | null;
  onClose: () => void;
}

const REASONS = ['Physical count correction', 'Damaged / write-off', 'Stolen', 'Lost', 'Found', 'Opening balance', 'Other'];

export function AdjustStockModal({ open, row, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fe = useFieldErrors<'qty' | 'reason' | 'barcode'>();
  // Step 1: pick Addition / Deduction. Step 2: the form for that choice.
  const [direction, setDirection] = useState<'in' | 'out' | null>(null);
  const [qty, setQty] = useState('');
  const [barcode, setBarcode] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [reasonSel, setReasonSel] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [refDoc, setRefDoc] = useState('');

  // Addition is a two-phase scan-confirmed flow (like GRN): after labels are
  // generated we move to 'scan'; nothing is added to stock until every printed
  // label is scanned and Add to Stock is pressed.
  const [phase, setPhase] = useState<'form' | 'scan'>('form');
  const [generated, setGenerated] = useState<{ serials: string[]; quantities: number[]; item: AdjustLabelItem } | null>(null);
  const [scanned, setScanned] = useState<Set<string>>(new Set());
  const [scanInput, setScanInput] = useState('');
  const [showLabels, setShowLabels] = useState(false);
  const [committed, setCommitted] = useState(false);
  // Deduction: the specific in-warehouse serials the user picks to remove.
  const [selectedSerials, setSelectedSerials] = useState<Set<string>>(new Set());
  // Addition: pieces each new label holds (one entry per label; "per barcode" fills them all).
  const [perLabel, setPerLabel] = useState('1');
  const [labelQtys, setLabelQtys] = useState<string[]>([]);
  // Deduction: pieces to take off each selected barcode (defaults to all it holds).
  const [deductQty, setDeductQty] = useState<Record<string, string>>({});

  function resetForm() {
    setQty('');
    setBarcode(row?.barcode ?? '');
    setLocationCode('');
    setReasonSel('');
    setCustomReason('');
    setRemarks('');
    setRefDoc('');
    setPhase('form');
    setGenerated(null);
    setScanned(new Set());
    setScanInput('');
    setShowLabels(false);
    setCommitted(false);
    setSelectedSerials(new Set());
    setPerLabel('1');
    setLabelQtys([]);
    setDeductQty({});
    fe.clear();
  }

  // Keep one quantity box per label as the label count changes (new ones take "per barcode").
  const labelCount = Number.isInteger(Number(qty)) && Number(qty) > 0 && Number(qty) <= 500 ? Number(qty) : 0;
  useEffect(() => {
    setLabelQtys((prev) => Array.from({ length: labelCount }, (_, i) => prev[i] ?? perLabel));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelCount]);
  function setAllPerLabel(v: string) {
    setPerLabel(v);
    setLabelQtys((prev) => prev.map(() => v));
  }
  const additionPcs = labelQtys.reduce((a, v) => a + (Number(v) || 0), 0);

  useEffect(() => {
    if (open) {
      setDirection(null);
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row]);

  function pick(dir: 'in' | 'out') {
    setDirection(dir);
    resetForm();
  }

  const reasonText = reasonSel === 'Other' ? customReason.trim() : reasonSel;
  const finalReason = [reasonText, remarks.trim()].filter(Boolean).join(' — ');

  const locationsQuery = useQuery({
    queryKey: ['locations-flat'],
    queryFn: () => listLocations({}),
    enabled: open && direction === 'in',
    staleTime: 60_000,
  });
  const locationOptions = useMemo(
    () => (locationsQuery.data ?? []).map((l) => ({ value: l.code, label: `${l.code} · ${l.name}` })),
    [locationsQuery.data],
  );

  // Deduction: the item's in-warehouse serials, so the user picks exactly which
  // barcodes to remove (no blind FIFO drop).
  const serialsQuery = useQuery({
    queryKey: ['wh-serials-for-item', row?.itemId],
    queryFn: () => listWarehouseSerials({ itemId: row!.itemId, status: 'InWarehouse' }),
    enabled: open && direction === 'out' && !!row,
  });
  const serialList = useMemo(() => serialsQuery.data?.rows ?? [], [serialsQuery.data]);
  const hasSerials = serialList.length > 0;
  const holdsOf = (sn: string) => Number(serialList.find((s) => s.serialNo === sn)?.qty ?? 1);
  const toggleSerial = (sn: string) =>
    setSelectedSerials((prev) => {
      const next = new Set(prev);
      if (next.has(sn)) next.delete(sn);
      else next.add(sn);
      return next;
    });
  // Pieces to take off a selected barcode — defaults to everything it holds.
  const takeOf = (sn: string) => Number(deductQty[sn] ?? holdsOf(sn));
  const deductPcs = [...selectedSerials].reduce((a, sn) => a + (takeOf(sn) || 0), 0);

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ['warehouse-stock'] });
    qc.invalidateQueries({ queryKey: ['warehouse-stock-kpis'] });
    qc.invalidateQueries({ queryKey: ['warehouse-ledger'] });
    qc.invalidateQueries({ queryKey: ['wh-serials'] });
  }

  // Deduction — immediate, no labels. When the item has tracked barcodes the
  // user picks exactly which serials to remove; otherwise it falls back to a
  // plain qty deduction (untracked items).
  const deductMut = useMutation({
    mutationFn: () =>
      hasSerials
        ? deductAdjustmentStock({
            itemId: row!.itemId,
            serialNos: [...selectedSerials],
            quantities: [...selectedSerials].map(takeOf),
            reason: finalReason,
            refDoc: refDoc || undefined,
            barcode: barcode.trim() || undefined,
          })
        : adjustWarehouseStock({
            itemId: row!.itemId,
            direction: 'out',
            qty: Number(qty),
            reason: finalReason,
            refDoc: refDoc || undefined,
            barcode: barcode.trim() || undefined,
          }),
    onSuccess: () => {
      toast.push(
        hasSerials
          ? `${deductPcs} ${row?.unit ?? 'pcs'} deducted from ${selectedSerials.size} barcode(s) for ${row?.itemName}`
          : `${Number(qty)} unit(s) deducted for ${row?.itemName}`,
        'success',
      );
      invalidateAll();
      onClose();
    },
    onError: (e) => toast.push(apiError(e, 'Failed'), 'error'),
  });

  // Addition step 1 — mint the labels (no stock change), open the print sheet.
  const prepareMut = useMutation({
    mutationFn: () => prepareAdjustmentLabels({ itemId: row!.itemId, qty: Number(qty), quantities: labelQtys.map(Number), barcode: barcode.trim() || undefined }),
    onSuccess: (r) => {
      setGenerated({ serials: r.serials, quantities: r.quantities ?? r.serials.map(() => 1), item: r.item });
      setScanned(new Set());
      setPhase('scan');
      setShowLabels(true);
      qc.invalidateQueries({ queryKey: ['wh-serials'] }); // Pending serials now exist
    },
    onError: (e) => toast.push(apiError(e, 'Failed'), 'error'),
  });

  // Addition step 2 — commit the scanned labels into stock.
  const commitMut = useMutation({
    mutationFn: () =>
      commitAdjustmentAddition({
        itemId: row!.itemId,
        serialNos: generated!.serials,
        reason: finalReason,
        refDoc: refDoc || undefined,
        locationCode: locationCode || undefined,
      }),
    onSuccess: () => {
      setCommitted(true);
      const pcs = (generated?.quantities ?? []).reduce((a, q) => a + q, 0);
      toast.push(`${pcs} ${row?.unit ?? 'pcs'} added to stock on ${generated?.serials.length ?? 0} barcode(s) for ${row?.itemName}`, 'success');
      invalidateAll();
      onClose();
    },
    onError: (e) => toast.push(apiError(e, 'Failed'), 'error'),
  });

  // Generate labels: validate qty + reason, then mint the Pending serials.
  function generate() {
    if (!row) return;
    const n = Number(qty);
    if (!fe.validate({
      qty: (!n || n <= 0 || !Number.isInteger(n))
        ? 'Whole number of barcodes > 0'
        : n > 500
          ? 'At most 500 barcodes at a time'
          : labelQtys.some((v) => !(Number(v) > 0)) && 'Every barcode needs a quantity greater than 0',
      reason: !reasonSel
        ? 'Reason is required for audit trail'
        : reasonSel === 'Other' && !customReason.trim()
          ? 'Enter a custom reason'
          : false,
    })) return;
    prepareMut.mutate();
  }

  function deduct() {
    if (!row) return;
    const reasonErr = !reasonSel
      ? 'Reason is required for audit trail'
      : reasonSel === 'Other' && !customReason.trim()
        ? 'Enter a custom reason'
        : false;
    if (hasSerials) {
      // Serial-select mode: at least one barcode must be picked.
      if (reasonErr) { fe.validate({ reason: reasonErr }); return; }
      if (selectedSerials.size === 0) {
        toast.push('Select at least one barcode to deduct', 'warning');
        return;
      }
      const bad = [...selectedSerials].find((sn) => !(takeOf(sn) > 0) || takeOf(sn) > holdsOf(sn));
      if (bad) {
        toast.push(`${bad}: quantity must be between 1 and ${holdsOf(bad)}`, 'warning');
        return;
      }
    } else {
      const n = Number(qty);
      if (!fe.validate({
        qty: (!n || n <= 0 || n > row.currentQty) && `Qty must be between 1 and ${row.currentQty}`,
        reason: reasonErr,
      })) return;
    }
    deductMut.mutate();
  }

  // Scan a printed label into the batch. Must be one of the generated serials.
  function handleScan(raw: string) {
    const code = raw.trim();
    if (!code || !generated) return;
    if (!generated.serials.includes(code)) {
      toast.push(`"${code}" is not one of this batch's labels`, 'error');
      setScanInput('');
      return;
    }
    if (scanned.has(code)) {
      toast.push(`"${code}" already scanned`, 'warning');
      setScanInput('');
      return;
    }
    setScanned((s) => new Set(s).add(code));
    setScanInput('');
  }

  const totalGen = generated?.serials.length ?? 0;
  const allScanned = totalGen > 0 && scanned.size === totalGen;

  // Discard un-committed labels when leaving the scan step or closing.
  function discardIfNeeded() {
    if (generated && !committed) {
      discardAdjustmentLabels(generated.serials).catch(() => {});
      qc.invalidateQueries({ queryKey: ['wh-serials'] });
    }
  }

  function handleClose() {
    discardIfNeeded();
    onClose();
  }

  function backFromScan() {
    discardIfNeeded();
    setGenerated(null);
    setScanned(new Set());
    setScanInput('');
    setPhase('form');
  }

  const isIn = direction === 'in';

  return (
    <>
    <Modal
      open={open}
      title={row ? `Adjust Stock — ${row.itemName}` : 'Adjust Stock'}
      onClose={handleClose}
      size="md"
      footer={
        direction === null ? (
          <Button variant="glass" onClick={handleClose}>Cancel</Button>
        ) : isIn && phase === 'scan' ? (
          <>
            <Button variant="glass" onClick={backFromScan}>
              <span className="inline-flex items-center gap-1"><ArrowLeft size={14} /> Back</span>
            </Button>
            <Button
              variant="primary"
              onClick={() => commitMut.mutate()}
              disabled={commitMut.isPending || !allScanned || !locationCode}
              title={!allScanned ? 'Scan every printed label first' : !locationCode ? 'Pick a location to place the stock' : 'Add the scanned units to stock'}
            >
              {commitMut.isPending ? 'Adding…' : `Add to Stock (${scanned.size}/${totalGen})`}
            </Button>
          </>
        ) : (
          <>
            <Button variant="glass" onClick={() => setDirection(null)}>
              <span className="inline-flex items-center gap-1"><ArrowLeft size={14} /> Back</span>
            </Button>
            {isIn ? (
              <Button variant="primary" onClick={generate} disabled={prepareMut.isPending}>
                {prepareMut.isPending ? 'Generating…' : 'Generate Barcodes'}
              </Button>
            ) : (
              <Button
                variant="danger"
                onClick={deduct}
                disabled={deductMut.isPending || (hasSerials && selectedSerials.size === 0)}
              >
                {deductMut.isPending
                  ? 'Saving…'
                  : hasSerials
                    ? `Deduct ${deductPcs} ${row?.unit ?? 'pcs'} (${selectedSerials.size} barcode${selectedSerials.size === 1 ? '' : 's'})`
                    : 'Deduct from Stock'}
              </Button>
            )}
          </>
        )
      }
    >
      {row && (
        <div className="grid grid-cols-1 gap-3">
          <div className="rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)] p-2 text-xs">
            <span className="text-[10px] uppercase text-[var(--text-muted)]">Current on hand</span>{' '}
            <strong>{row.currentQty} {row.unit}</strong>
            <span className="ml-2 text-[10px] uppercase text-[var(--text-muted)]">Code</span>{' '}
            <strong className="text-accent">{row.itemCode}</strong>
          </div>

          {/* STEP 1 — choose Addition or Deduction */}
          {direction === null && (
            <div className="grid grid-cols-2 gap-3 py-2">
              <button
                type="button"
                onClick={() => pick('in')}
                className="flex flex-col items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-soft)] p-5 transition hover:border-success hover:bg-success/[0.06]"
              >
                <PlusCircle size={30} className="text-success" />
                <span className="text-sm font-extrabold text-[var(--text-primary)]">Addition</span>
                <span className="text-[11px] text-[var(--text-muted)]">Generate labels, scan, add stock</span>
              </button>
              <button
                type="button"
                onClick={() => pick('out')}
                className="flex flex-col items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-soft)] p-5 transition hover:border-danger hover:bg-danger/[0.06]"
              >
                <MinusCircle size={30} className="text-danger" />
                <span className="text-sm font-extrabold text-[var(--text-primary)]">Deduction</span>
                <span className="text-[11px] text-[var(--text-muted)]">Remove damaged / lost / stolen</span>
              </button>
            </div>
          )}

          {/* ADDITION — step form (generate labels) */}
          {isIn && phase === 'form' && (
            <>
              <div className="flex items-center gap-2 rounded-md bg-success/10 px-3 py-1.5 text-xs font-bold text-success">
                <PlusCircle size={15} /> Addition — 1. generate &amp; print labels
              </div>

              <div className="grid grid-cols-2 gap-3">
                <FormGroup
                  label="Number of barcodes"
                  required
                  error={fe.errors.qty}
                  hint="One label is printed per barcode"
                >
                  <Input
                    type="number"
                    step="1"
                    min={1}
                    autoFocus
                    invalid={fe.has('qty')}
                    value={qty}
                    onChange={(e) => { setQty(e.target.value); fe.clear('qty'); }}
                  />
                </FormGroup>
                <FormGroup label={`Qty per barcode (${row.unit})`} required hint="Fills every barcode below — edit any one">
                  <Input type="number" min={0} step="any" value={perLabel} onChange={(e) => { setAllPerLabel(e.target.value); fe.clear('qty'); }} />
                </FormGroup>
              </div>

              {/* One quantity per barcode label. */}
              {labelQtys.length > 0 && (
                <div className="rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)] p-2">
                  <div className="mb-1.5 flex items-center justify-between text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                    <span>Qty on each barcode</span>
                    <span className="text-[var(--text-secondary)]">{labelQtys.length} barcode{labelQtys.length === 1 ? '' : 's'} · {additionPcs} {row.unit}</span>
                  </div>
                  <div className="grid max-h-40 grid-cols-2 gap-1.5 overflow-y-auto sm:grid-cols-3">
                    {labelQtys.map((v, i) => (
                      <label key={i} className="flex items-center gap-1.5 rounded bg-[var(--card-bg)] px-2 py-1 text-xs">
                        <span className="w-7 shrink-0 text-[10px] font-bold text-[var(--text-muted)]">#{i + 1}</span>
                        <input
                          type="number" min={0} step="any" value={v}
                          onChange={(e) => { const nv = e.target.value; setLabelQtys((prev) => prev.map((x, j) => (j === i ? nv : x))); fe.clear('qty'); }}
                          className="w-full min-w-0 rounded border border-[var(--border-color)] bg-[var(--input-bg)] px-1.5 py-0.5 text-right text-xs font-semibold text-[var(--text-primary)]"
                        />
                      </label>
                    ))}
                  </div>
                  <div className="mt-1.5 text-[10px] text-[var(--text-muted)]">Stock is added only after you scan the printed labels in.</div>
                </div>
              )}

              <FormGroup label="Item Barcode" hint="Scan or type to confirm the item (must match this SKU)">
                <div className="relative">
                  <ScanLine size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                  <Input value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder="Scan / type the SKU barcode" className="!pl-8 font-mono" autoComplete="off" />
                </div>
              </FormGroup>

              <div className="grid grid-cols-2 gap-3">
                <FormGroup label="Reason" required error={fe.errors.reason}>
                  <Select value={reasonSel} onChange={(e) => { setReasonSel(e.target.value); fe.clear('reason'); }}>
                    <option value="">— Select —</option>
                    {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </Select>
                </FormGroup>
                <FormGroup label="Reference Doc (optional)">
                  <Input placeholder="Audit / FIR / claim no" value={refDoc} onChange={(e) => setRefDoc(e.target.value)} />
                </FormGroup>
              </div>

              {reasonSel === 'Other' && (
                <FormGroup label="Custom Reason" required error={fe.errors.reason}>
                  <Input autoFocus invalid={fe.has('reason')} placeholder="Type the reason for this adjustment" value={customReason} onChange={(e) => { setCustomReason(e.target.value); fe.clear('reason'); }} />
                </FormGroup>
              )}

              <FormGroup label="Remarks (optional)" hint="Extra detail recorded in the audit trail">
                <Textarea rows={2} placeholder="e.g. found during cycle count in bin A-3…" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
              </FormGroup>
            </>
          )}

          {/* ADDITION — scan step */}
          {isIn && phase === 'scan' && generated && (
            <>
              <div className="flex items-center gap-2 rounded-md bg-success/10 px-3 py-1.5 text-xs font-bold text-success">
                <ScanLine size={15} /> Addition — 2. scan the printed labels into a bin
              </div>

              <div className="flex items-center justify-between rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)] p-2 text-xs">
                <span><strong>{totalGen}</strong> label{totalGen === 1 ? '' : 's'} generated</span>
                <Button variant="glass" size="sm" onClick={() => setShowLabels(true)}>
                  <span className="inline-flex items-center gap-1"><Tags size={13} /> Print Labels</span>
                </Button>
              </div>

              <FormGroup label="Place at Location" required hint="Bin the scanned units go into — required for stock to go on-hand">
                <SearchableSelect
                  options={locationOptions}
                  value={locationCode}
                  onChange={(v) => setLocationCode(v === '' ? '' : String(v))}
                  placeholder="Search / scan a bin…"
                  leftIcon={<ScanLine size={15} />}
                />
              </FormGroup>

              <FormGroup label="Scan Barcodes" required hint={`Scan each printed label — ${scanned.size} of ${totalGen} done`}>
                <div className="relative">
                  <ScanLine size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                  <Input
                    autoFocus
                    value={scanInput}
                    onChange={(e) => setScanInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleScan(scanInput); } }}
                    placeholder="Scan a label serial and press Enter"
                    className="!pl-8 font-mono"
                    autoComplete="off"
                    disabled={allScanned}
                  />
                </div>
              </FormGroup>

              <div className="rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)] p-2">
                <div className="mb-1.5 flex items-center justify-between text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                  <span>Labels</span>
                  <span className={allScanned ? 'text-success' : 'text-[var(--text-secondary)]'}>{scanned.size}/{totalGen} scanned</span>
                </div>
                <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto">
                  {generated.serials.map((sn, i) => {
                    const done = scanned.has(sn);
                    return (
                      <span
                        key={sn}
                        className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px] ${done ? 'bg-success/15 text-success' : 'bg-white/[0.06] text-[var(--text-secondary)]'}`}
                      >
                        {done && <Check size={10} />}{sn}
                        <span className="font-sans font-bold">· {generated.quantities[i]} {row.unit}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* DEDUCTION form */}
          {!isIn && direction === 'out' && (
            <>
              <div className="flex items-center gap-2 rounded-md bg-danger/10 px-3 py-1.5 text-xs font-bold text-danger">
                <MinusCircle size={15} /> Deduction — removing stock
              </div>

              {/* Tracked item → pick exact barcodes. Untracked → plain qty. */}
              {serialsQuery.isLoading ? (
                <div className="rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)] p-3 text-center text-xs text-[var(--text-muted)]">
                  Loading barcodes…
                </div>
              ) : hasSerials ? (
                <FormGroup
                  label="Select barcodes to deduct"
                  required
                  hint={`${selectedSerials.size} of ${serialList.length} selected · ${deductPcs} ${row.unit} to deduct — a barcode keeps whatever you don't take off`}
                >
                  <div className="rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)] p-2">
                    <div className="mb-1.5 flex items-center justify-between text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                      <span>In-warehouse serials</span>
                      <button
                        type="button"
                        className="text-accent hover:underline"
                        onClick={() =>
                          setSelectedSerials(
                            selectedSerials.size === serialList.length
                              ? new Set()
                              : new Set(serialList.map((s) => s.serialNo)),
                          )
                        }
                      >
                        {selectedSerials.size === serialList.length ? 'Clear all' : 'Select all'}
                      </button>
                    </div>
                    <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                      {serialList.map((s) => {
                        const on = selectedSerials.has(s.serialNo);
                        const holds = Number(s.qty ?? 1);
                        return (
                          <div
                            key={s.serialNo}
                            className={`flex items-center gap-2 rounded px-2 py-1 text-xs transition ${on ? 'bg-danger/[0.08] ring-1 ring-danger/40' : 'hover:bg-white/[0.05]'}`}
                          >
                            <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
                              <input type="checkbox" checked={on} onChange={() => toggleSerial(s.serialNo)} className="accent-danger" />
                              <span className="truncate font-mono text-[var(--text-primary)]">{s.serialNo}</span>
                              <span className="shrink-0 text-[10px] font-semibold text-[var(--text-muted)]">holds {holds} {row.unit}</span>
                            </label>
                            {s.location && (
                              <span className="shrink-0 text-[10px] text-[var(--text-muted)]">{s.location.code}</span>
                            )}
                            {/* Pieces to take off this barcode — only once it's ticked. */}
                            {on && (
                              <span className="flex shrink-0 items-center gap-1">
                                <input
                                  type="number" min={0} max={holds} step="any"
                                  value={deductQty[s.serialNo] ?? String(holds)}
                                  onChange={(e) => setDeductQty((prev) => ({ ...prev, [s.serialNo]: e.target.value }))}
                                  className="w-14 rounded border border-danger/40 bg-[var(--input-bg)] px-1.5 py-0.5 text-right text-xs font-semibold text-[var(--text-primary)]"
                                  title={`Qty to deduct from ${s.serialNo} (max ${holds})`}
                                />
                                <span className="text-[10px] text-[var(--text-muted)]">/ {holds}</span>
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </FormGroup>
              ) : (
                <FormGroup label={`Quantity (${row.unit})`} required error={fe.errors.qty} hint="This item has no tracked barcodes — deduct by quantity">
                  <Input type="number" step="1" autoFocus invalid={fe.has('qty')} value={qty} onChange={(e) => { setQty(e.target.value); fe.clear('qty'); }} />
                </FormGroup>
              )}

              {/* SKU-confirm scan is only meaningful for untracked qty deduction —
                  picking a tracked serial already identifies the item. */}
              {!hasSerials && (
                <FormGroup label="Item Barcode" hint="Scan or type to confirm the item (must match this SKU)">
                  <div className="relative">
                    <ScanLine size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                    <Input value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder="Scan / type the SKU barcode" className="!pl-8 font-mono" autoComplete="off" />
                  </div>
                </FormGroup>
              )}

              <div className="grid grid-cols-2 gap-3">
                <FormGroup label="Reason" required error={fe.errors.reason}>
                  <Select value={reasonSel} onChange={(e) => { setReasonSel(e.target.value); fe.clear('reason'); }}>
                    <option value="">— Select —</option>
                    {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </Select>
                </FormGroup>
                <FormGroup label="Reference Doc (optional)">
                  <Input placeholder="Audit / FIR / claim no" value={refDoc} onChange={(e) => setRefDoc(e.target.value)} />
                </FormGroup>
              </div>

              {reasonSel === 'Other' && (
                <FormGroup label="Custom Reason" required error={fe.errors.reason}>
                  <Input autoFocus invalid={fe.has('reason')} placeholder="Type the reason for this adjustment" value={customReason} onChange={(e) => { setCustomReason(e.target.value); fe.clear('reason'); }} />
                </FormGroup>
              )}

              <FormGroup label="Remarks (optional)" hint="Extra detail recorded in the audit trail">
                <Textarea rows={2} placeholder="e.g. rat-damaged in bin A-3, insurance claim filed…" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
              </FormGroup>
            </>
          )}
        </div>
      )}
    </Modal>

    <AdjustLabelsModal
      open={showLabels}
      serials={generated?.serials ?? []}
      item={generated?.item ?? null}
      onClose={() => setShowLabels(false)}
    />
    </>
  );
}
